import React from 'react'
import { useLogger, useSwanStyleKeys, useUserContext, useSwanFontKeys } from '@vp/ubik-context'
import { BannerWrapper, type ITrackingProps, type DefaultContentfulBannerWrapperProps } from '@vp/banner-wrapper'
import { BoundedContent, Column, Row, SWAN_STYLE_KEY_MAP, SwanFontNameEnum } from '@vp/swan'
import { createOnClickTrackingHandler, getCtaTrackingConfig } from '../../Analytics/trackingUtils'
import { TrackingPageZone } from '../../Analytics/trackingTypes'
import { BANNER_MAP_SCHEMA } from '../../utils/trackingSchemas'
import { ContentfulLink, ContentfulContextProvider, DebugSection, MCS_REACT_TOOLS_SWAN_STYLES } from '@vp/mcs-react-tools'
import { FragmentLogger } from '@vp/ubik-logging'
import { type IdebugData } from '../../shared/types'
import type { ExplanationRecord } from '@vp/mcs'
import { isDebugModeEnabled } from '../../utils/debugMode'

export interface IBannerProps {
  bannerProps: DefaultContentfulBannerWrapperProps[]
  locale: string
  bannerPosition: string
  sectionId: string
  contentfulMetadata: {
    spaceId?: string
    environment: string
    contentful_id: string
    contentfulName: string
  }
  debugData: IdebugData
  ubikTracking?: {
    clickTriggerId?: string
  }
  explanation?: ExplanationRecord
}

export const Banner = ({ bannerProps, bannerPosition, sectionId, locale, contentfulMetadata, debugData, ubikTracking, explanation }: IBannerProps) => {
  const logger = useLogger() as FragmentLogger
  const userContext = useUserContext()
  logger.setContext({
    bannerPosition,
    userContext,
    entry: contentfulMetadata?.contentful_id,
    name: 'Deals-Banner-Fragment',
    'vista.team_tag': 'VPD-0001',
  })

  const trackingPageZone = TrackingPageZone[bannerPosition]
  useSwanFontKeys([SwanFontNameEnum.TiemposRegular])

  useSwanStyleKeys([
    SWAN_STYLE_KEY_MAP.core,
    SWAN_STYLE_KEY_MAP.grid,
    SWAN_STYLE_KEY_MAP.icon,
    SWAN_STYLE_KEY_MAP.button,
    SWAN_STYLE_KEY_MAP.carousel,
    SWAN_STYLE_KEY_MAP.standardTile,
    SWAN_STYLE_KEY_MAP.popover,
    SWAN_STYLE_KEY_MAP.hidden,
    SWAN_STYLE_KEY_MAP.visible,
    SWAN_STYLE_KEY_MAP.utility,
    SWAN_STYLE_KEY_MAP.banner,
    ...(isDebugModeEnabled(userContext) ? MCS_REACT_TOOLS_SWAN_STYLES : []),
  ])

  const trackingMethod = (event: React.MouseEvent<Element, MouseEvent>, { destinationUrl, linkIdentifier, ctaPosition, openInNewTab }: ITrackingProps) => {
    const trackingConfig = getCtaTrackingConfig({
      sourcePagePath: '/offers',
      destinationUrl: destinationUrl ?? '',
      pageZone: trackingPageZone,
      linkIdentifier,
      ctaPosition,
    })
    if (!trackingConfig) {
      return
    }
    const ubikEventTriggerId = ubikTracking?.clickTriggerId

    const trackingAndNavigation = createOnClickTrackingHandler({
      trackingConfig,
      logger,
      schema: BANNER_MAP_SCHEMA,
      href: destinationUrl,
      openLinkNewTab: openInNewTab,
      callback: undefined,
      ubikClickTriggerId: ubikEventTriggerId,
    })
    if (trackingAndNavigation) {
      trackingAndNavigation(event)
    }
  }
  const search = typeof window !== 'undefined' ? window.location.search : ''

  return (
    <div id={sectionId}>
      {bannerProps && bannerProps.length > 0 && (
        <ContentfulContextProvider space={contentfulMetadata?.spaceId || ''}>
          <ContentfulLink id={contentfulMetadata?.contentful_id} />
          <DebugSection contentfulMetadata={contentfulMetadata} availability={debugData} search={search} explanation={explanation} />
          <BoundedContent>
            <Row marginBottom="2">
              <Column span={12} padding={0}>
                <BannerWrapper bannerProps={bannerProps} trackingHandler={trackingMethod} locale={locale} />
              </Column>
            </Row>
          </BoundedContent>
        </ContentfulContextProvider>
      )}
    </div>
  )
}
